import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { apiGet } from "../lib/customFetch";
import type { LegalDocumentBundle } from "./types";

interface LegalDocumentsState {
  bundle: LegalDocumentBundle | null;
  error: boolean;
  loading: boolean;
  retry: () => void;
}

const LegalDocumentsContext = createContext<LegalDocumentsState>({
  bundle: null,
  error: false,
  loading: true,
  retry: () => undefined,
});

export function LegalDocumentsProvider({ children }: { children: ReactNode }) {
  const [bundle, setBundle] = useState<LegalDocumentBundle | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setError(false);
    apiGet<LegalDocumentBundle>("/api/legal")
      .then((result) => {
        if (active) setBundle(result);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [attempt]);

  const retry = () => setAttempt((current) => current + 1);

  return createElement(
    LegalDocumentsContext.Provider,
    { value: { bundle, error, loading: bundle === null && !error, retry } },
    children,
  );
}

export function useLegalDocuments(): LegalDocumentsState {
  return useContext(LegalDocumentsContext);
}
